import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRight, Plus } from "lucide-react";

const STATUS_COLOR = { Confirmed: "text-[#00F0FF]", Rumour: "text-gray-500", Loan: "text-[#F4D03F]" };

export default function Transfers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [transfers, setTransfers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ player_id: "", from_team_id: "", to_team_id: "", status: "Confirmed" });

  const load = () => {
    api.get("/transfers").then((r) => setTransfers(r.data));
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/teams").then((r) => setTeams(r.data));
  };
  useEffect(() => { load(); }, []);

  const teamById = (id) => teams.find((t) => t.id === id);
  const playerById = (id) => players.find((p) => p.id === id);

  const submit = async () => {
    try {
      await api.post("/transfers", { ...form, fee: "Undisclosed" });
      toast.success("Transfer registered.");
      setOpen(false);
      setForm({ player_id: "", from_team_id: "", to_team_id: "", status: "Confirmed" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="transfers-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[#F4D03F] font-mono tracking-[0.3em] text-xs mb-2">/ MARKET DESK</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Transfers</h1>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-transfer-button"
                className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold"
              >
                <Plus className="w-4 h-4 mr-2" /> New transfer
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight">Register transfer</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Player</Label>
                  <Select value={form.player_id} onValueChange={(v) => setForm({ ...form, player_id: v })}>
                    <SelectTrigger data-testid="transfer-player" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                      <SelectValue placeholder="Select player" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#101015] border-[#232330] text-white">
                      {players.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.handle}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">From</Label>
                    <Select value={form.from_team_id} onValueChange={(v) => setForm({ ...form, from_team_id: v })}>
                      <SelectTrigger data-testid="transfer-from" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                        <SelectValue placeholder="Team" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-gray-400">To</Label>
                    <Select value={form.to_team_id} onValueChange={(v) => setForm({ ...form, to_team_id: v })}>
                      <SelectTrigger data-testid="transfer-to" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                        <SelectValue placeholder="Team" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#101015] border-[#232330] text-white">
                        {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="transfer-status" className="rounded-none bg-[#05050A] border-[#232330] mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#101015] border-[#232330] text-white">
                      <SelectItem value="Confirmed">Confirmed</SelectItem>
                      <SelectItem value="Rumour">Rumour</SelectItem>
                      <SelectItem value="Loan">Loan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={submit}
                  data-testid="transfer-submit"
                  className="w-full rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold"
                >
                  Register
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="border border-[#232330] overflow-x-auto">
        <table className="w-full min-w-[700px] text-sm">
          <thead>
            <tr className="border-b border-[#232330] bg-[#0a0a12]">
              {["Date", "Player", "From", "To", "Status"].map((h) => (
                <th key={h} className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {transfers.map((t) => (
              <tr key={t.id} data-testid={`transfer-row-${t.id}`} className="border-b border-[#232330]">
                <td className="px-4 py-3 font-mono text-gray-500 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 font-heading font-bold uppercase tracking-tight">{playerById(t.player_id)?.handle || "—"}</td>
                <td className="px-4 py-3 text-gray-400">{teamById(t.from_team_id)?.tag || "FA"}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-2 text-white">
                    <ArrowRight className="w-3 h-3 text-cyan-400" /> {teamById(t.to_team_id)?.tag}
                  </span>
                </td>
                <td className={`px-4 py-3 font-mono text-xs uppercase tracking-widest ${STATUS_COLOR[t.status]}`}>{t.status}</td>
              </tr>
            ))}
            {transfers.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No transfer activity yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
